// Detalle de servicio en overlay responsive (Modal desktop / Drawer móvil).
// Ficha limpia: identidad → plan/precio → ubicación → fechas → metadata.
// La jerarquía se logra con tipografía y espaciado — sin cards anidadas,
// bordes pesados ni fondos diferenciados dentro del contenido.
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Radio, Space, Typography, message, theme } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { DetalleOverlay } from '../../../components/DetalleOverlay';
import { EmptyState } from '../../../components/EmptyState';
import { StatusTag } from '../../../components/StatusTag';
import { tonoEstadoServicio } from '../../../lib/estado-servicio-tono';
import { ConfirmDeleteModal } from '../../../components/ConfirmDeleteModal';
import { ServicioEditModal } from './ServicioEditModal';
import { useServicio, useUpdateServicio } from '../api/useServicios';
import { useCatalogos } from '../../catalogos/api/useCatalogos';
import { useHasPermission } from '../../../auth/useHasPermission';
import { useT } from '../../../locales';
import { DATE_FORMAT, parseDateOnly } from '../../../lib/date';
import { coordenadasValidas } from '../../../lib/geo';
import { VerMapaButton } from '../../../components/VerMapaButton';
import { CATEGORIA_ESTADO_SERVICIO, formatCurrency, Permission, transicionesPermitidas } from '@aid/shared';
import type { EstadoServicioKey } from '@aid/shared';

const { Text } = Typography;

/** Modo de "dar de baja": el destino (key del catálogo) se resuelve en el handler. */
type ModoBaja = 'corte' | 'inmediata';

/** Destino de cada modo según la máquina de estados (nunca asumido en la UI). */
const KEY_POR_MODO: Record<ModoBaja, EstadoServicioKey> = {
  corte: 'BAJA_AL_CORTE',
  inmediata: 'BAJA',
};

/** Copy del confirm cuando solo queda una modalidad legal de baja. */
const CLAVE_MENSAJE_UNICA: Record<ModoBaja, string> = {
  corte: 'pages.servicios.darDeBajaSoloCorte',
  inmediata: 'pages.servicios.darDeBajaSoloInmediata',
};

/**
 * Copy del confirm de "dar de baja" según las opciones legales: dos
 * opciones explican la elección; una sola aclara la modalidad, con copy
 * específico para adelantar la baja desde BAJA_AL_CORTE a hoy.
 */
function claveMensajeBaja(opciones: ModoBaja[], estado: EstadoServicioKey | null): string {
  if (opciones.length !== 1 || opciones[0] === undefined) {
    return 'pages.servicios.darDeBajaConfirm';
  }
  if (estado === 'BAJA_AL_CORTE') {
    return 'pages.servicios.darDeBajaAdelantar';
  }
  return CLAVE_MENSAJE_UNICA[opciones[0]];
}

interface ServicioDetailModalProps {
  id: number | null;
  onClose: () => void;
}

// Header de sección del detalle: caption 12/600 secondary, sin divider
// horizontal — las secciones se delimitan con aire + este rótulo.
function SeccionHeader({ children }: { children: string }) {
  const { token } = theme.useToken();
  return (
    <Text
      type="secondary"
      style={{
        display: 'block',
        fontSize: 12,
        fontWeight: 600,
        lineHeight: 1.4,
        marginBottom: 12,
        color: token.colorTextSecondary,
      }}
    >
      {children}
    </Text>
  );
}

// Columna de la banda de fechas: label 12 secondary + valor 14. Las columnas
// 2+ se separan con hairline y padding lateral.
function ColumnaFecha({
  label,
  valor,
  separador = false,
}: {
  label: string;
  valor: string | null;
  separador?: boolean;
}) {
  const t = useT();
  const { token } = theme.useToken();
  return (
    <div
      style={{
        minWidth: 110,
        padding: separador ? '0 16px' : '0 16px 0 0',
        borderLeft: separador ? `1px solid ${token.colorSplit}` : 'none',
        // El hairline es una guía sutil, no un muro de formulario.
        opacity: separador ? 0.85 : 1,
      }}
    >
      <Text
        type="secondary"
        style={{ display: 'block', fontSize: 12, lineHeight: 1.4, marginBottom: 4 }}
      >
        {label}
      </Text>
      {valor ? (
        <Text
          style={{
            display: 'block',
            fontSize: 14,
            fontWeight: 500,
            lineHeight: 1.4,
            color: token.colorText,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {valor}
        </Text>
      ) : (
        <Text type="secondary" style={{ display: 'block', fontSize: 13, lineHeight: 1.4 }}>
          {t('pages.servicios.sinDefinir')}
        </Text>
      )}
    </div>
  );
}

// Badge pequeño de estado: única aparición del estado en la ficha.
const badgeStyle = {
  fontSize: 12,
  fontWeight: 600,
  padding: '2px 12px',
  borderRadius: 999,
  lineHeight: 1.5,
};

export function ServicioDetailModal({ id, onClose }: ServicioDetailModalProps) {
  const t = useT();
  const { data: servicio, isLoading, error } = useServicio(id ?? 0);
  const updateServicio = useUpdateServicio();
  const canUpdate = useHasPermission(Permission.SERVICIOS_UPDATE);
  const { token } = theme.useToken();

  const [bajaOpen, setBajaOpen] = useState(false);
  const [bajaModo, setBajaModo] = useState<ModoBaja>('corte');
  const [reactivarOpen, setReactivarOpen] = useState(false);
  const [cancelarBajaOpen, setCancelarBajaOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  // Estado → ids desde el catálogo (mismo mecanismo que CatalogoSelect en
  // ServicioForm/ServicioEditModal): la UI resuelve la key contra
  // ESTADO_SERVICIO y nunca inventa ids. Si la key no se resuelve, la
  // acción queda deshabilitada.
  const { data: estadosCatalogo } = useCatalogos(CATEGORIA_ESTADO_SERVICIO);
  const idEstadoPorKey = new Map<string, number>();
  for (const item of estadosCatalogo ?? []) {
    if (item.key !== null) idEstadoPorKey.set(item.key.toUpperCase(), item.id);
  }
  const idBaja = idEstadoPorKey.get('BAJA');
  const idBajaAlCorte = idEstadoPorKey.get('BAJA_AL_CORTE');
  const idActivo = idEstadoPorKey.get('ACTIVO');

  const esBaja = servicio?.estado?.key?.toUpperCase() === 'BAJA';

  // Opciones de "dar de baja" derivadas de la máquina de estados, nunca
  // asumidas: intersección de las transiciones manuales legales del estado
  // actual con {BAJA, BAJA_AL_CORTE}, EXCLUYENDO el estado en que ya está el
  // servicio — ofrecerlo sería un no-op legal que no cambia nada ni deja
  // rastro (p.ej. "al corte" para un servicio ya en BAJA_AL_CORTE).
  // PENDIENTE solo ofrece BAJA (inmediata, sin radio); BAJA_AL_CORTE solo
  // adelanta la baja a hoy; sin opciones legales la acción queda deshabilitada.
  const estadoActualKey = (servicio?.estado?.key?.toUpperCase() ??
    null) as EstadoServicioKey | null;
  const legales = transicionesPermitidas(estadoActualKey, 'manual');
  const opcionesBaja = (['corte', 'inmediata'] as ModoBaja[]).filter(
    (modo) => KEY_POR_MODO[modo] !== estadoActualKey && legales.includes(KEY_POR_MODO[modo]),
  );
  const idDestinoPorModo = (modo: ModoBaja) => (modo === 'corte' ? idBajaAlCorte : idBaja);
  // Deshabilitada sin opciones legales o sin ningún id resoluble del catálogo.
  const bajaDeshabilitada =
    opcionesBaja.length === 0 ||
    opcionesBaja.every((modo) => idDestinoPorModo(modo) === undefined);
  // Modo efectivo: el elegido solo si sigue legal y resuelto; si no, el
  // primero legal resuelto (recomendada: al corte cuando existe).
  const modoEfectivo: ModoBaja =
    opcionesBaja.includes(bajaModo) && idDestinoPorModo(bajaModo) !== undefined
      ? bajaModo
      : (opcionesBaja.find((modo) => idDestinoPorModo(modo) !== undefined) ??
        opcionesBaja[0] ??
        bajaModo);
  const idDestinoBaja = idDestinoPorModo(modoEfectivo);
  // Copy del confirm: con dos opciones se explica la elección; con una
  // sola se aclara qué modalidad aplica (BAJA_AL_CORTE usa su copy propio
  // de "adelantar a hoy", no el genérico de única opción).
  const mensajeBaja = t(claveMensajeBaja(opcionesBaja, estadoActualKey));

  const fecha = (value: Date | string | null | undefined) =>
    // Date-only contract: legacy ISO-midnight values recover the calendar date.
    parseDateOnly(value)?.format(DATE_FORMAT) ?? null;

  // The API returns the service point or the client point as its effective location.
  const ubicacion = coordenadasValidas(servicio?.latitud, servicio?.longitud);
  const tipo = servicio?.tipoServicio?.nombre;
  const nombrePlan = servicio?.plan?.nombre;

  const municipio = servicio?.municipio?.value ?? servicio?.municipio?.key;
  const departamento = servicio?.departamento?.value ?? servicio?.departamento?.key;
  const tieneUbicacion = Boolean(
    ubicacion || municipio || departamento || servicio?.direccion || servicio?.referencia,
  );

  // Con 1 solo período el estado ya está en el badge de la identidad — el
  // historial solo se muestra cuando hay más de un cambio de estado.
  const historial = servicio?.historialEstados ?? [];
  // Igual criterio para los planes: con 1 solo período el plan ya está en la
  // sección superior; el historial se muestra cuando hubo más de un plan.
  const historialPlanes = servicio?.historialPlanes ?? [];

  // "Dar de baja": cambia el estado a BAJA_AL_CORTE (al corte) o BAJA
  // (inmediata) a través de la máquina de estados del backend (PATCH); la
  // modalidad sale de las opciones legales derivadas arriba.
  const handleDarDeBaja = async () => {
    if (!servicio || idDestinoBaja === undefined) return;
    try {
      await updateServicio.mutateAsync({ id: servicio.id, dto: { idEstado: idDestinoBaja } });
      setBajaOpen(false);
      message.success(t('pages.servicios.darDeBajaSuccess'));
    } catch {
      message.error(t('pages.servicios.darDeBajaError'));
    }
  };

  // "Reactivar": BAJA -> ACTIVO por el trigger de reactivación del backend,
  // que también limpia la fechaFin. El modal solo pide el estado destino.
  const handleReactivar = async () => {
    if (!servicio || idActivo === undefined) return;
    try {
      await updateServicio.mutateAsync({ id: servicio.id, dto: { idEstado: idActivo } });
      setReactivarOpen(false);
      message.success(t('pages.servicios.reactivarSuccess'));
    } catch {
      message.error(t('pages.servicios.reactivarError'));
    }
  };

  // "Cancelar baja programada": solo BAJA_AL_CORTE -> ACTIVO (manual,
  // efecto 'clear': el backend elimina la fechaFin). Falla cerrado si el
  // catálogo no resuelve el id de ACTIVO: nunca se inventa un destino.
  const handleCancelarBaja = async () => {
    if (!servicio || idActivo === undefined) return;
    try {
      await updateServicio.mutateAsync({ id: servicio.id, dto: { idEstado: idActivo } });
      setCancelarBajaOpen(false);
      message.success(t('pages.servicios.cancelarBajaSuccess'));
    } catch {
      message.error(t('pages.servicios.cancelarBajaError'));
    }
  };

  const footer = (
    <Space wrap>
      {canUpdate && servicio && !esBaja && (
        <Button
          danger
          disabled={bajaDeshabilitada}
          onClick={() => {
            setBajaModo(opcionesBaja[0] ?? 'corte');
            setBajaOpen(true);
          }}
        >
          {t('pages.servicios.darDeBaja')}
        </Button>
      )}
      {canUpdate && servicio && esBaja && (
        <Button
          type="primary"
          disabled={idActivo === undefined}
          onClick={() => setReactivarOpen(true)}
        >
          {t('pages.servicios.reactivar')}
        </Button>
      )}
      {canUpdate && servicio && estadoActualKey === 'BAJA_AL_CORTE' && (
        <Button
          type="primary"
          disabled={idActivo === undefined}
          onClick={() => setCancelarBajaOpen(true)}
        >
          {t('pages.servicios.cancelarBaja')}
        </Button>
      )}
      <Button onClick={onClose}>{t('common.close')}</Button>
      {canUpdate && servicio && (
        <Button type="primary" icon={<EditOutlined />} onClick={() => setEditOpen(true)}>
          {t('common.edit')}
        </Button>
      )}
    </Space>
  );

  const titulo =
    id !== null ? `${t('pages.servicios.detailId', { id })}${tipo ? ` · ${tipo}` : ''}` : undefined;

  return (
    <>
      <DetalleOverlay
        open={id !== null}
        onClose={onClose}
        title={titulo}
        footer={footer}
        loading={isLoading}
      >
        {error || !servicio ? (
          <EmptyState
            title={t('pages.servicios.notFound')}
            description={t('pages.servicios.notFoundDetail')}
          />
        ) : (
          <div>
            {/* Identidad: nombre del cliente como texto plano (no es link) a la
                izquierda; el estado como badge pequeño a la derecha. Debajo,
                una línea secundaria con el código (ip) y el link discreto. */}
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 16,
                alignItems: 'flex-start',
                justifyContent: 'space-between',
              }}
            >
              {servicio.cliente ? (
                <div style={{ minWidth: 0, flex: 1 }}>
                  <Text
                    style={{
                      display: 'block',
                      fontSize: 22,
                      fontWeight: 600,
                      lineHeight: 1.3,
                      color: token.colorText,
                      overflowWrap: 'break-word',
                    }}
                  >
                    {servicio.cliente.nombre ?? servicio.cliente.ip ?? '—'}
                  </Text>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 4 }}>
                    <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.4 }}>
                      {servicio.cliente.ip ?? '—'}
                    </Text>
                    <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.4 }}>
                      ·
                    </Text>
                    <Link
                      to={`/clientes/${servicio.cliente.id}`}
                      style={{ fontSize: 12, lineHeight: 1.4, color: token.colorPrimary }}
                    >
                      {t('components.verPerfil')}
                    </Link>
                  </div>
                </div>
              ) : (
                // Fallback último: el ref de cliente viene null solo si la
                // relación no se resolvió (join perdido). El producto prohíbe
                // el ID como identificador, pero ante un dato inconsistente
                // mostrar el ID es mejor que una ficha vacía.
                <Text
                  style={{
                    display: 'block',
                    fontSize: 22,
                    fontWeight: 600,
                    lineHeight: 1.3,
                    color: token.colorText,
                  }}
                >
                  {`#${servicio.idCliente}`}
                </Text>
              )}

              {servicio.estado ? (
                <StatusTag tone={tonoEstadoServicio(servicio.estado.key)} style={badgeStyle}>
                  {servicio.estado.value ?? servicio.estado.key ?? `#${servicio.estado.id}`}
                </StatusTag>
              ) : (
                <StatusTag tone="info" style={badgeStyle}>
                  —
                </StatusTag>
              )}
            </div>

            {/* Plan: sección limpia sin card. Fila 1: nombre del plan.
                Fila 2: precio destacado + "por mes". Fila 3: comisión
                (no compite con el nombre del plan). */}
            <section
              style={{
                marginTop: 20,
                paddingTop: 16,
                borderTop: `1px solid ${token.colorSplit}`,
              }}
            >
              <SeccionHeader>{t('pages.servicios.plan')}</SeccionHeader>
              <Text
                style={{
                  display: 'block',
                  fontSize: 16,
                  fontWeight: 600,
                  lineHeight: 1.4,
                  color: token.colorText,
                  overflowWrap: 'break-word',
                }}
              >
                {nombrePlan ?? `#${servicio.idPlan}`}
              </Text>
              {servicio.plan?.precio != null && (
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginTop: 6 }}>
                  <span
                    style={{
                      fontSize: 20,
                      fontWeight: 700,
                      lineHeight: 1.3,
                      fontVariantNumeric: 'tabular-nums',
                      color:
                        Number(servicio.plan.precio) < 0
                          ? token.colorError
                          : Number(servicio.plan.precio) > 0
                            ? token.colorSuccess
                            : token.colorText,
                    }}
                  >
                    {formatCurrency(Number(servicio.plan.precio))}
                  </span>
                  <Text
                    type="secondary"
                    style={{ fontSize: 12.5, fontWeight: 500, lineHeight: 1.4 }}
                  >
                    {t('pages.servicios.porMes')}
                  </Text>
                </div>
              )}
              {servicio.plan && (
                <Text
                  type="secondary"
                  style={{
                    display: 'block',
                    fontSize: 12.5,
                    fontWeight: 500,
                    lineHeight: 1.4,
                    marginTop: 6,
                  }}
                >
                  {servicio.plan.generaComision === true
                    ? `${t('pages.servicios.comision')} ${formatCurrency(Number(servicio.plan.comision ?? 0))}`
                    : t('pages.servicios.sinComision')}
                </Text>
              )}
            </section>

            {tieneUbicacion && (
              <section
                style={{
                  marginTop: 20,
                  paddingTop: 16,
                  borderTop: `1px solid ${token.colorSplit}`,
                }}
              >
                {/* Header de sección con el mapa como acción, no como dato. */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12,
                    marginBottom: 12,
                  }}
                >
                  <Text type="secondary" style={{ fontSize: 12, fontWeight: 600, lineHeight: 1.4 }}>
                    {t('pages.servicios.ubicacion')}
                  </Text>
                  {ubicacion && <VerMapaButton latitud={ubicacion.lat} longitud={ubicacion.lng} />}
                </div>

                {(municipio || departamento) && (
                  <Text
                    style={{
                      display: 'block',
                      fontSize: 15,
                      fontWeight: 600,
                      color: token.colorText,
                      overflowWrap: 'break-word',
                    }}
                  >
                    {[municipio, departamento].filter(Boolean).join(', ')}
                  </Text>
                )}
                {servicio.direccion && (
                  <div style={{ marginTop: 10 }}>
                    <Text
                      type="secondary"
                      style={{ display: 'block', fontSize: 12, lineHeight: 1.4, marginBottom: 2 }}
                    >
                      {t('pages.servicios.direccion')}
                    </Text>
                    <Text
                      style={{
                        fontSize: 14,
                        color: token.colorText,
                        lineHeight: 1.5,
                        overflowWrap: 'break-word',
                      }}
                    >
                      {servicio.direccion}
                    </Text>
                  </div>
                )}
                {servicio.referencia && (
                  <div style={{ marginTop: 10 }}>
                    <Text
                      type="secondary"
                      style={{ display: 'block', fontSize: 12, lineHeight: 1.4, marginBottom: 2 }}
                    >
                      {t('pages.servicios.referencia')}
                    </Text>
                    <Text
                      style={{
                        fontSize: 14,
                        color: token.colorText,
                        lineHeight: 1.5,
                        overflowWrap: 'break-word',
                      }}
                    >
                      {servicio.referencia}
                    </Text>
                  </div>
                )}
              </section>
            )}

            <section
              style={{
                marginTop: 20,
                paddingTop: 16,
                borderTop: `1px solid ${token.colorSplit}`,
              }}
            >
              <SeccionHeader>{t('pages.servicios.fechasClave')}</SeccionHeader>
              <div style={{ display: 'flex', flexWrap: 'wrap', rowGap: 16 }}>
                <ColumnaFecha
                  label={t('pages.servicios.fechaInicio')}
                  valor={fecha(servicio.fechaInicio)}
                />
                {servicio.fechaFin && (
                  <ColumnaFecha
                    label={t('pages.servicios.fechaFin')}
                    valor={fecha(servicio.fechaFin)}
                    separador
                  />
                )}
              </div>
            </section>

            <Text
              type="secondary"
              style={{
                display: 'block',
                fontSize: 12,
                lineHeight: 1.5,
                marginTop: 20,
                marginBottom: 12,
              }}
            >
              {[
                `${t('common.createdAt')} ${fecha(servicio.fechaCreacion) ?? '—'}`,
                `${t('common.updatedAt')} ${fecha(servicio.fechaModificacion) ?? '—'}`,
                servicio.usuarioModifica ? `por ${servicio.usuarioModifica}` : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </Text>

            {historial.length > 1 && (
              <section
                style={{
                  marginTop: 20,
                  paddingTop: 16,
                  borderTop: `1px solid ${token.colorSplit}`,
                }}
              >
                <SeccionHeader>{t('pages.servicios.historialTitle')}</SeccionHeader>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                    columnGap: 16,
                    paddingBottom: 8,
                  }}
                >
                  <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.4 }}>
                    {t('pages.servicios.status')}
                  </Text>
                  <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.4 }}>
                    {t('pages.servicios.historialPeriodo')}
                  </Text>
                  <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.4 }}>
                    {t('pages.servicios.historialUsuario')}
                  </Text>
                </div>
                {[...historial].reverse().map((item, index) => (
                  <div
                    key={item.id}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                      columnGap: 16,
                      padding: '10px 0',
                      alignItems: 'center',
                      borderBottom:
                        index < historial.length - 1 ? `1px solid ${token.colorSplit}` : 'none',
                    }}
                  >
                    <StatusTag
                      tone={tonoEstadoServicio(item.estadoKey)}
                      style={{ fontSize: 12, padding: '0 10px' }}
                    >
                      {item.estadoValue ?? item.estadoKey}
                    </StatusTag>
                    <Text
                      style={{
                        fontSize: 14,
                        color: token.colorText,
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {fecha(item.fechaInicio) ?? '—'}
                    </Text>
                    <Text style={{ fontSize: 14, color: token.colorText }}>
                      {item.usuarioModifica}
                    </Text>
                  </div>
                ))}
              </section>
            )}

            {historialPlanes.length > 1 && (
              <section
                style={{
                  marginTop: 20,
                  paddingTop: 16,
                  borderTop: `1px solid ${token.colorSplit}`,
                }}
              >
                <SeccionHeader>{t('pages.servicios.historialPlanesTitle')}</SeccionHeader>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                    columnGap: 16,
                    paddingBottom: 8,
                  }}
                >
                  <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.4 }}>
                    {t('pages.servicios.plan')}
                  </Text>
                  <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.4 }}>
                    {t('pages.servicios.historialPeriodo')}
                  </Text>
                  <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.4 }}>
                    {t('pages.servicios.historialUsuario')}
                  </Text>
                </div>
                {[...historialPlanes].reverse().map((item, index) => (
                  <div
                    key={item.id}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                      columnGap: 16,
                      padding: '10px 0',
                      alignItems: 'center',
                      borderBottom:
                        index < historialPlanes.length - 1
                          ? `1px solid ${token.colorSplit}`
                          : 'none',
                    }}
                  >
                    <Text style={{ fontSize: 14, color: token.colorText, fontWeight: 500 }}>
                      {item.planNombre ?? `#${item.idPlan}`}
                    </Text>
                    <Text
                      style={{
                        fontSize: 14,
                        color: token.colorText,
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {`${fecha(item.fechaDesde) ?? '—'} → ${
                        item.fechaHasta
                          ? fecha(item.fechaHasta)
                          : t('pages.servicios.historialPlanActual')
                      }`}
                    </Text>
                    <Text style={{ fontSize: 14, color: token.colorText }}>
                      {item.usuarioModifica ?? '—'}
                    </Text>
                  </div>
                ))}
              </section>
            )}
          </div>
        )}
      </DetalleOverlay>
      <ConfirmDeleteModal
        open={bajaOpen}
        onConfirm={handleDarDeBaja}
        onCancel={() => setBajaOpen(false)}
        loading={updateServicio.isPending}
        title={t('pages.servicios.darDeBajaTitle')}
        message={mensajeBaja}
        okText={t('pages.servicios.darDeBajaOk')}
      >
        {/* El estado destino sale del catálogo: si la key no se resuelve,
            la opción se deshabilita en vez de inventar un id. Solo se
            muestran las modalidades legales para el estado actual. */}
        {opcionesBaja.length > 1 && (
          <Radio.Group
            value={modoEfectivo}
            onChange={(e) => setBajaModo(e.target.value as ModoBaja)}
          >
            {opcionesBaja.map((modo) => (
              <div key={modo}>
                <Radio value={modo} disabled={idDestinoPorModo(modo) === undefined}>
                  {modo === 'corte'
                    ? t('pages.servicios.darDeBajaCorte')
                    : t('pages.servicios.darDeBajaInmediata')}
                </Radio>
                <div
                  style={{
                    marginLeft: 24,
                    marginTop: 4,
                    marginBottom: modo === 'corte' ? 12 : 0,
                  }}
                >
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {modo === 'corte'
                      ? t('pages.servicios.darDeBajaCorteHelp')
                      : t('pages.servicios.darDeBajaInmediataHelp')}
                  </Text>
                </div>
              </div>
            ))}
          </Radio.Group>
        )}
      </ConfirmDeleteModal>
      <ConfirmDeleteModal
        open={reactivarOpen}
        onConfirm={handleReactivar}
        onCancel={() => setReactivarOpen(false)}
        loading={updateServicio.isPending}
        title={t('pages.servicios.reactivarTitle')}
        message={t('pages.servicios.reactivarConfirm')}
        okText={t('pages.servicios.reactivarOk')}
        okDanger={false}
      />
      <ConfirmDeleteModal
        open={cancelarBajaOpen}
        onConfirm={handleCancelarBaja}
        onCancel={() => setCancelarBajaOpen(false)}
        loading={updateServicio.isPending}
        title={t('pages.servicios.cancelarBajaTitle')}
        message={t('pages.servicios.cancelarBajaConfirm')}
        okText={t('pages.servicios.cancelarBajaOk')}
        okDanger={false}
      />
      {editOpen && servicio && (
        <ServicioEditModal id={servicio.id} onClose={() => setEditOpen(false)} />
      )}
    </>
  );
}
